import { useState } from 'react';
import { Icon } from '../components/icons';
import { Badge, Button, ErrorNote, Modal, Segmented, TD, TH, Textarea, cx } from '../components/ui';
import { api, type ImportSummary } from '../lib/api';

export function ImportDialog({ jobId, open, onClose, onImported }: { jobId: string; open: boolean; onClose: () => void; onImported: () => void }) {
  const [mode, setMode] = useState<'file' | 'paste'>('file');
  const [file, setFile] = useState<File | null>(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [result, setResult] = useState<ImportSummary | null>(null);
  const [over, setOver] = useState(false);

  const close = () => {
    setFile(null);
    setText('');
    setResult(null);
    setError(null);
    onClose();
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      let summary: ImportSummary;
      if (mode === 'file') {
        const form = new FormData();
        form.append('file', file!);
        summary = await api.post<ImportSummary>(`/api/jobs/${jobId}/import`, form);
      } else {
        summary = await api.post<ImportSummary>(`/api/jobs/${jobId}/import/paste`, { text });
      }
      setResult(summary);
      onImported();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title="Import leads"
      description="Every lead is cleaned, de-duplicated and pre-screened on import."
      size="lg"
      footer={
        result ? (
          <Button variant="primary" onClick={close}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={close}>Cancel</Button>
            <Button variant="primary" icon="upload" onClick={submit} loading={busy} disabled={mode === 'file' ? !file : !text.trim()}>
              Import
            </Button>
          </>
        )
      }
    >
      {!result ? (
        <div className="space-y-4">
          <Segmented
            label="Import method"
            size="md"
            value={mode}
            onChange={setMode}
            options={[
              { value: 'file', label: 'Upload a file', icon: 'upload' },
              { value: 'paste', label: 'Paste from the sheet', icon: 'clipboard' },
            ]}
          />
          {mode === 'file' ? (
            <label
              onDragOver={(e) => {
                e.preventDefault();
                setOver(true);
              }}
              onDragLeave={() => setOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setOver(false);
                setFile(e.dataTransfer.files?.[0] ?? null);
              }}
              className={cx(
                'flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-12 text-center transition',
                over ? 'border-accent bg-accent-soft' : 'border-border-strong bg-surface-2 hover:border-accent/60',
              )}
            >
              <input type="file" accept=".xlsx,.csv,.tsv,.txt" className="hidden" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
              <span className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-surface text-accent shadow-card ring-1 ring-border">
                <Icon name={file ? 'check' : 'upload'} className="h-5 w-5" />
              </span>
              <span className="text-sm font-semibold text-fg">{file ? file.name : 'Drop an .xlsx or .csv file here, or click to choose'}</span>
              <span className="mt-1 text-xs text-muted-fg">From Google Sheets: File → Download → Microsoft Excel (.xlsx) or CSV</span>
            </label>
          ) : (
            <Textarea
              rows={10}
              placeholder="Select the rows in Google Sheets or Excel, including the header row, copy, and paste here."
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="font-mono !text-xs"
            />
          )}
          <p className="text-xs leading-relaxed text-muted-fg">
            Columns are recognised by name (Name, Contact number, Email ID, Year of Experience, Sector…). Duplicates by phone or email are skipped, including candidates already on this job, so importing the same sheet twice is safe.
          </p>
          <ErrorNote error={error} />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-3 text-center">
            {(
              [
                ['Rows', result.totalRows, 'text-fg'],
                ['Imported', result.imported, 'text-success'],
                ['Duplicates', result.duplicates, 'text-warning'],
                ['Rejected', result.invalid, 'text-destructive'],
              ] as const
            ).map(([label, value, colour]) => (
              <div key={label} className="rounded-md bg-surface-2 p-3 ring-1 ring-border">
                <div className={cx('display text-[28px] font-medium leading-none tabular', colour)}>{value}</div>
                <div className="mt-1.5 font-mono text-[10px] uppercase tracking-wider text-muted-fg">{label}</div>
              </div>
            ))}
          </div>
          {result.unmappedColumns.length > 0 && <p className="text-xs text-muted-fg">Kept as extra details: {result.unmappedColumns.join(', ')}</p>}
          <div className="overflow-hidden rounded-md ring-1 ring-border">
            <table className="w-full text-sm">
              <thead className="bg-surface-2">
                <tr>
                  <th className={TH}>Row</th>
                  <th className={TH}>Candidate</th>
                  <th className={TH}>Outcome</th>
                </tr>
              </thead>
              <tbody>
                {result.outcomes
                  .filter((o) => o.status !== 'imported' || (o.warnings?.length ?? 0) > 0)
                  .map((o) => (
                    <tr key={o.row} className="border-t border-border">
                      <td className={cx(TD, 'font-mono text-xs text-muted-fg')}>{o.row}</td>
                      <td className={TD}>{o.lead?.name ?? o.name ?? '—'}</td>
                      <td className={TD}>
                        {o.status === 'imported' && <Badge tone="amber">Imported with warnings</Badge>}
                        {o.status === 'duplicate' && <Badge tone="slate">Duplicate of {o.duplicateOf}</Badge>}
                        {o.status === 'invalid' && <Badge tone="red">Rejected</Badge>}
                        <span className="ml-2 text-xs text-muted-fg">{o.status === 'imported' ? o.warnings?.join('; ') : o.reason}</span>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
            {result.outcomes.every((o) => o.status === 'imported' && !o.warnings?.length) && <p className="px-3 py-5 text-center text-sm text-muted-fg">Every row imported cleanly.</p>}
          </div>
        </div>
      )}
    </Modal>
  );
}
